import type { Hex, SignerConfig, SignerVaultInfo } from '../types/signers';
import { emblemPost } from './http';
import { bytesToHex, normalizeTxForEmblem } from './utils';
import { fetchVaultInfo } from './vault';

// ethers v6 compatible interface (like solana pattern - no runtime dependency)
export interface EmblemEthersWallet {
  getAddress(): Promise<string>;
  signMessage(message: string | Uint8Array): Promise<string>;
  signTypedData(
    domain: unknown,
    types: Record<string, Array<{ name: string; type: string }>>,
    value: Record<string, unknown>
  ): Promise<string>;
  signTransaction(tx: unknown): Promise<string>;
  sendTransaction(tx: unknown): Promise<unknown>;
  connect(provider: unknown): EmblemEthersWallet;
  provider: unknown;
  // Additional methods
  initialize(): Promise<void>;
  getVaultId(): string;
  setChainId(chainId: number): void;
  getChainId(): number;
  signAndBroadcast(transaction: unknown, waitForReceipt?: boolean): Promise<string>;
}

export async function toEthersWallet(
  config: SignerConfig,
  provider?: unknown | null,
  infoOverride?: SignerVaultInfo
): Promise<EmblemEthersWallet> {
  // Dynamic import - only loads ethers when this function is called
  // String concatenation prevents bundlers from statically analyzing the import
  // DO NOT CHANGE THIS PATTERN - using a literal import('ethers') breaks Next.js/Webpack builds
  // when ethers is not installed. The variable indirection is intentional. - Shannon
  let ethers: typeof import('ethers');
  try {
    const moduleName = 'ethers';
    ethers = await import(/* @vite-ignore */ moduleName);
  } catch {
    throw new Error('ethers is required for toEthersWallet(). Install it with: npm install ethers');
  }

  const { AbstractSigner, resolveAddress } = ethers;
  type AbstractProvider = import('ethers').AbstractProvider;
  type TransactionRequest = import('ethers').TransactionRequest;
  type TransactionResponse = import('ethers').TransactionResponse;
  type TransactionLike = import('ethers').TransactionLike<string>;
  type TypedDataDomain = import('ethers').TypedDataDomain;
  type TypedDataField = import('ethers').TypedDataField;

  const info = infoOverride ?? (await fetchVaultInfo(config));

  // Class defined inside function after dynamic import
  class EmblemEthersWalletImpl extends AbstractSigner {
    private readonly _config: SignerConfig;
    private _address: Hex | null = null;
    private _vaultId: string | null = null;
    private _chainId = 1;
    private _initPromise?: Promise<void>;

    constructor(
      signerConfig: SignerConfig,
      signerProvider?: AbstractProvider | null,
      seed?: { address?: Hex; vaultId?: string; chainId?: number }
    ) {
      super(signerProvider ?? null);
      this._config = signerConfig;
      if (seed?.address) this._address = seed.address;
      if (seed?.vaultId) this._vaultId = seed.vaultId;
      if (seed?.chainId) this._chainId = seed.chainId;
    }

    async initialize(): Promise<void> {
      if (this._initPromise) return this._initPromise;

      this._initPromise = fetchVaultInfo(this._config)
        .then((vaultInfo) => {
          this._address = vaultInfo.evmAddress;
          this._vaultId = vaultInfo.vaultId;
        })
        .catch((err) => {
          this._initPromise = undefined;
          throw err;
        });

      return this._initPromise;
    }

    async getAddress(): Promise<string> {
      if (!this._address) await this.initialize();
      return this._address!;
    }

    getVaultId(): string {
      if (!this._vaultId) throw new Error('Wallet not initialized. Call initialize() first.');
      return this._vaultId;
    }

    setChainId(chainId: number): void {
      this._chainId = chainId;
    }

    getChainId(): number {
      return this._chainId;
    }

    connect(newProvider: AbstractProvider): EmblemEthersWalletImpl {
      if (!newProvider) throw new Error('Provider cannot be null');
      return new EmblemEthersWalletImpl(this._config, newProvider, {
        address: this._address ?? undefined,
        vaultId: this._vaultId ?? undefined,
        chainId: this._chainId,
      });
    }

    async signMessage(message: string | Uint8Array): Promise<string> {
      if (!this._vaultId) await this.initialize();
      const payload = typeof message === 'string' ? message : bytesToHex(message);
      const data = await emblemPost<{ signerAddress: string; signature: Hex }>(
        '/sign-eth-message',
        { vaultId: this._vaultId!, message: payload },
        this._config
      );
      return data.signature;
    }

    async signTypedData(
      domain: TypedDataDomain,
      types: Record<string, Array<TypedDataField>>,
      value: Record<string, unknown>
    ): Promise<string> {
      if (!this._vaultId) await this.initialize();
      const cleanTypes = { ...types };
      if (cleanTypes && (cleanTypes as Record<string, unknown>).EIP712Domain) {
        delete (cleanTypes as Record<string, unknown>).EIP712Domain;
      }
      const data = await emblemPost<{ signerAddress: string; signature: Hex }>(
        '/sign-typed-message',
        { vaultId: this._vaultId!, domain, types: cleanTypes, message: value },
        this._config
      );
      return data.signature;
    }

    async _signTypedData(
      domain: TypedDataDomain,
      types: Record<string, Array<TypedDataField>>,
      value: Record<string, unknown>
    ): Promise<string> {
      return this.signTypedData(domain, types, value);
    }

    async signTransaction(tx: TransactionRequest): Promise<string> {
      if (!this._vaultId) await this.initialize();
      const from = (tx as Record<string, unknown>).from as string | undefined;
      const addr = await this.getAddress();

      if (from && from.toLowerCase() !== addr.toLowerCase()) {
        throw new Error('transaction from does not match signer address');
      }

      const toSign = this.provider
        ? ({ ...await this.populateTransaction(tx) } as Record<string, unknown>)
        : ({ ...tx } as Record<string, unknown>);
      if (toSign.from) delete toSign.from;
      if (!('to' in toSign) || !toSign.to) {
        throw new Error("Transaction must have a 'to' address");
      }
      if (toSign.nonce === undefined || toSign.nonce === null) {
        throw new Error('Transaction must have a nonce');
      }
      const normalized = normalizeTxForEmblem(toSign);
      const resp = await emblemPost<{ signedTransaction: Hex }>(
        '/sign-eth-tx',
        { vaultId: this._vaultId!, transaction: normalized, options: { chainId: this._chainId } },
        this._config
      );
      return resp.signedTransaction;
    }

    async sendTransaction(tx: TransactionRequest): Promise<TransactionResponse> {
      if (!this.provider) throw new Error('Provider required to send transaction');
      const signed = await this.signTransaction(tx);
      return await this.provider.broadcastTransaction(signed);
    }

    async populateTransaction(transaction: TransactionRequest): Promise<TransactionLike> {
      const tx = { ...transaction } as TransactionRequest;
      if (!this.provider) throw new Error('Provider required to populate transaction');
      const fromAddress = tx.from
        ? await resolveAddress(tx.from, this.provider)
        : await this.getAddress();

      let chainId: bigint;
      if (!tx.chainId) {
        const network = await this.provider.getNetwork();
        chainId = network.chainId;
        this._chainId = Number(network.chainId);
      } else {
        chainId = BigInt(tx.chainId);
        this._chainId = Number(tx.chainId);
      }

      const nonce =
        tx.nonce != null
          ? Number(tx.nonce)
          : await this.provider.getTransactionCount(fromAddress, 'pending');
      const toAddress = tx.to ? await resolveAddress(tx.to, this.provider) : null;
      const value = tx.value ? BigInt(tx.value.toString()) : 0n;

      let gasLimit: bigint;
      if (!tx.gasLimit) {
        try {
          gasLimit = await this.provider.estimateGas({ ...tx, from: fromAddress });
        } catch {
          gasLimit = 21000n;
        }
      } else {
        gasLimit = BigInt(tx.gasLimit.toString());
      }

      let gasPrice: bigint | null = null;
      if (!tx.gasPrice && tx.type !== 2) {
        const feeData = await this.provider.getFeeData();
        gasPrice = feeData.gasPrice ?? null;
      } else if (tx.gasPrice) {
        gasPrice = BigInt(tx.gasPrice.toString());
      }

      const populated: TransactionLike = {
        from: fromAddress,
        to: toAddress,
        value,
        nonce,
        gasLimit,
        data: tx.data as string | undefined,
        chainId,
        type: tx.type || undefined,
      };
      if (gasPrice !== null) populated.gasPrice = gasPrice;
      if (tx.maxFeePerGas) populated.maxFeePerGas = BigInt(tx.maxFeePerGas.toString());
      if (tx.maxPriorityFeePerGas)
        populated.maxPriorityFeePerGas = BigInt(tx.maxPriorityFeePerGas.toString());
      return populated;
    }

    async signAndBroadcast(
      transaction: TransactionRequest,
      waitForReceipt: boolean = false
    ): Promise<string> {
      if (!this.provider) throw new Error('Provider required to send transaction');
      const signed = await this.signTransaction(transaction);
      const resp = await this.provider.broadcastTransaction(signed);
      const hash = resp.hash as string;
      if (waitForReceipt) {
        await this.provider.waitForTransaction(hash);
      }
      return hash;
    }
  }

  return new EmblemEthersWalletImpl(config, provider as AbstractProvider | null, {
    address: info.evmAddress,
    vaultId: info.vaultId,
  });
}
